import _ from 'lodash';
import React from 'react';

import { formatGeoRegionCity } from 'services/gis/geo-region-format';
import COUNTRIES from 'common/constants/countries';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import { IncludeExcludeLayout } from 'widgets-v6/layout';

function _getCountryName(code) {
    var country = _.find(COUNTRIES, function(c) {
        return c.id === code;
    });
    return country && country.text;
}

/**
 * Renders a geotarget country with optional include/exclude region lists.
 *
 * @param {Object} obj - Geotarget with country / include / exclude
 * @param {boolean} [compact=false]
 * @param {Object} [options]
 * @param {boolean} [options.allowIncludeExclude=true] - When false, include/exclude rows are struck through (countries stay normal)
 * @returns {JSX.Element}
 */
export default function formatGeoCountry(obj, compact = false, options = {}) {
    const allowIncludeExclude = options.allowIncludeExclude !== false;
    const includeExcludeStyle = allowIncludeExclude
        ? null
        : {
              textDecoration: 'line-through',
              color: 'gray',
          };

    var content,
        countryName = _getCountryName(obj.country);

    var includes, excludes;
    if (!_.isEmpty(obj.include)) {
        content = _.compact(_.map(obj.include, _.partial(formatGeoRegionCity, obj.country)));
        includes = (
            <div className="regions" key={_.uniqueId()} style={includeExcludeStyle}>
                <IncludeExcludeLayout type="Include" marginBottom compact={compact}>
                    <div title={content.join(', ')}>
                        {_.map(content, (place, i) => (
                            <div key={place + i}>{place}</div>
                        ))}
                    </div>
                </IncludeExcludeLayout>
            </div>
        );
    }

    if (!_.isEmpty(obj.exclude)) {
        content = _.compact(_.map(obj.exclude, _.partial(formatGeoRegionCity, obj.country)));
        excludes = (
            <div className="regions" style={includeExcludeStyle}>
                <IncludeExcludeLayout type="Exclude" compact={compact}>
                    <div title={content.join(', ')}>
                        {_.map(content, (place, i) => (
                            <div key={place + i}>{place}</div>
                        ))}
                    </div>
                </IncludeExcludeLayout>
            </div>
        );
    }

    return (
        <div key={_.uniqueId()}>
            <Box mb={2}>
                <Box mb={1}>
                    <Typography>{countryName}</Typography>
                </Box>
                {includes}
                {excludes}
            </Box>
        </div>
    );
}

/**
 * Plain-text lines for a geotarget country, used by the history Copy button.
 * The country and Include / Exclude headings stay; each region or city is its own line.
 *
 * @param {Object} obj - Geotarget with country / include / exclude
 * @returns {string[]} Lines in display order
 */
export function getGeoCountryClipboardLines(obj) {
    const asList = places => {
        if (Array.isArray(places)) {
            return places;
        }
        if (places !== undefined && places !== null && typeof places === 'object') {
            return Object.values(places);
        }
        return [];
    };
    const isEmptyCollection = value => {
        if (value === undefined || value === null) {
            return true;
        }
        if (Array.isArray(value) || typeof value === 'string') {
            return value.length === 0;
        }
        if (typeof value === 'object') {
            return Object.keys(value).length === 0;
        }
        return true;
    };
    const getPlaces = places =>
        asList(places)
            .map(place => formatGeoRegionCity(obj.country, place))
            .filter(Boolean);
    const lines = [_getCountryName(obj.country)];

    if (!isEmptyCollection(obj.include)) {
        lines.push('Include:', ...getPlaces(obj.include));
    }
    if (!isEmptyCollection(obj.exclude)) {
        lines.push('Exclude:', ...getPlaces(obj.exclude));
    }

    return lines.filter(Boolean);
}
